import { Box, Button, Grid, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Custom3dotString from "../../../../../components/Custom3dotString";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../../components/CustomTextField";
import SearchableSelect from "../../../../../components/Select/SearchableSelect";
import CustomSelect from "../../../../../components/Select/Select";
import {
  createPrescriptionWoLoader,
  getPetsCompliantSummaryWoLoader,
  updatePrescriptionByIdWoLoader,
} from "../../../../../redux/reducers/petSlice";
import { showSnackBar } from "../../../../../redux/reducers/snackSlice";
import { AppColors } from "../../../../../util/AppColors";
import { frequncyCheckList } from "../../../../../util/arrayList";
import { mealTypes, routeList } from "../../../../../util/dropList";
import { frequencyConEnum } from "../../../../../util/enum";
import { getRightPackageUom } from "../../../../../util/function";
import { refreshClinicStocksToSession } from "../../../../../util/refreshClinicStocksToSession";

const initValues = {
  plName: null,
  dosage: "",
  duration: "",
  frequency: [],
  meal: "",
  customFrequency: false,
  route: null,
  stockId: null,
  productId: null,
  serviceId: null,
  type: null,
  batchId: null,
  remark: "",
  sig: "",
  nameErr: false,
};

const AddPrescriptionDrawer = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(initValues);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = reqDet?.appointment;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinicStocks, clinicDet } = useMemo(() => {
    if (!mandateDataRaw) return { clinicStocks: [], clinicDet: {} };
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinicStocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
        clinicDet: reqData?.clinic ?? {},
      };
    } catch {
      return { clinicStocks: [], clinicDet: {} };
    }
  }, [mandateDataRaw]);

  useEffect(() => {
    if (!open) return;
    setValues(initValues);
    setSelectedItems([]);
    setIsSaving(false);
  }, [open]);

  const getPresList = () => {
    const categories = [
      "medication",
      "diet",
      "nutrition and supplements",
      "fluid therapy",
      "other parasite treatment",
      "deworming",
      "flea & tick treatment",
      "grooming & hygiene care",
    ];

    return (clinicStocks ?? [])
      ?.filter(
        (dta) =>
          dta?.active === "Y" &&
          dta?.billable &&
          categories.includes((dta?.category ?? "").toLowerCase()),
      )
      ?.map((li) => {
        const clinicbatches = li?.stockBatch?.filter(
          (btch) => btch?.createdBy === clinicDet?.id,
        );
        const totalQty = (clinicbatches ?? [])?.reduce(
          (acc, obj) => acc + Number(obj?.quantity ?? 0),
          0,
        );
        const isNearExpiry = (clinicbatches ?? [])?.some((batch) => {
          const expiryDate = batch?.expiryDate;
          if (!expiryDate?.length) return false;
          const today = moment().startOf("day");
          const expiry = moment(new Date(expiryDate)).startOf("day");
          if (expiry.isBefore(today)) return false;
          return expiry.diff(today, "days") < 30;
        });
        return {
          name: li?.tradeName ?? li?.productName,
          value: li?.tradeName ?? li?.productName,
          stockId: li?.stockId,
          type: li?.category,
          productName: li?.productName,
          tradeName: li?.tradeName,
          totalQty,
          isNearExpiry,
        };
      });
  };

  const getStockById = (stockId) =>
    clinicStocks?.find((stc) => stc?.stockId === stockId) ?? null;

  const getBatch = (stockId, batchId = null) => {
    if (!stockId) return null;
    const reqStock = getStockById(stockId);
    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id,
    );
    if (!batchId) return clinicBatches?.[0] ?? null;
    return (
      clinicBatches?.find((bat) => bat?.batchId === batchId) ??
      clinicBatches?.[0] ??
      null
    );
  };

  const onChangeValue = (name, value, addObj) => {
    setValues((prev) => {
      const reqObj = { ...prev, [name]: value };
      if (name === "frequency") {
        if (value?.includes?.("Custom")) {
          reqObj.frequency = "";
          reqObj.customFrequency = true;
        }
      }
      if (name === "plName" && addObj) {
        reqObj.nameErr = false;
        reqObj.type = addObj?.type ?? null;
        reqObj.stockId = addObj?.stockId ?? null;
        reqObj.productId = addObj?.productId ?? null;
        reqObj.serviceId = addObj?.serviceId ?? null;
        if (addObj?.stockId) {
          const reqBatch = clinicStocks
            ?.find((stc) => stc?.stockId === addObj?.stockId)
            ?.stockBatch?.filter(
              (btch) => btch?.createdBy === clinicDet?.id,
            )?.[0];
          reqObj.batchId = reqBatch?.batchId ?? null;
          reqObj.dosage = reqBatch?.dosage ?? prev?.dosage ?? "";
        }
      }
      return reqObj;
    });
  };

  const onApiCall = () => {
    if (appDet?.appointmentId) {
      dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
    }
  };

  const buildPresPayloadItem = (pre) => {
    const selStock = clinicStocks?.find((stc) => stc?.stockId === pre?.stockId);
    const btchDet = selStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id,
    )?.[0];
    const reqFreqList =
      Array.isArray(pre?.frequency) && pre?.frequency?.length > 0
        ? pre?.frequency?.map((fre) => frequencyConEnum[fre?.value ?? fre])
        : (pre?.frequencyDuration ?? []);
    const reqQty = 1;
    const reqObj = {
      administered: false,
      type: pre?.type ?? null,
      stockId: pre?.stockId,
      batchId: btchDet?.batchId ?? pre?.batchId ?? null,
      name: pre?.plName ?? pre?.name,
      dosage: pre?.dosage ?? "",
      duration: pre?.duration ?? "",
      route: pre?.route?.value ?? pre?.route ?? null,
      meal: pre?.meal,
      instruction: pre?.instruction ?? "",
      quantity: reqQty,
      subUnit: "Unit",
      morning:
        (reqFreqList?.length > 0 && reqFreqList?.includes("morning")) ?? false,
      afternoon:
        (reqFreqList?.length > 0 && reqFreqList?.includes("afternoon")) ??
        false,
      night:
        (reqFreqList?.length > 0 && reqFreqList?.includes("night")) ?? false,
      adminChargeAmount: 0,
      adminCharge: "N",
      percentage: false,
      delete: false,
      discontinued: "N",
      detailId: pre?.detailId ?? null,
      remark: pre?.remark ?? "",
      drugSchedule: selStock?.drugSchedule ?? null,
      hsnSacCode: selStock?.hsnSacCode ?? null,
      packageUom: btchDet?.salesUom ?? pre?.packageUom ?? null,
      expiryDate: btchDet?.expiryDate ?? pre?.expiryDate ?? null,
      batchNo: btchDet?.batchNo ?? pre?.batchNo ?? null,
      shortName: selStock?.shortName ?? null,
    };
    const taxPercent =
      (btchDet?.cgst ? Number(btchDet?.cgst) : 0) +
      (btchDet?.sgst ? Number(btchDet?.sgst) : 0);
    const reqPercent = 100 + Number(taxPercent);
    const unitPrice =
      (btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0) /
      (reqPercent / 100);
    const taxValue =
      Number(
        (btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0) -
          Number(unitPrice),
      ) ?? 0;
    reqObj.price = btchDet?.unitSellingPrice ?? pre?.price ?? 0;
    reqObj.mrpPrice = btchDet?.unitSellingPrice
      ? Number(btchDet?.unitSellingPrice)
      : Number(pre?.mrpPrice ?? 0);
    reqObj.mrpDiscount = 0;
    reqObj.tax = Number(taxValue) * reqQty;
    reqObj.total =
      (btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0) *
        Number(reqQty) ?? 0;
    reqObj.frequencyDuration = reqFreqList ?? [];
    if (pre?.productId) reqObj.productId = pre?.productId;
    if (pre?.serviceId) reqObj.serviceId = pre?.serviceId;
    return reqObj;
  };

  const mapExistingPresToForm = (li) => {
    const reqObj = {
      detailId: li?.id ?? li?.detailId ?? null,
      plName: li?.name,
      name: li?.name,
      dosage: li?.dosage,
      duration: li?.duration,
      meal: li?.meal,
      type: li?.type,
      stockId: li?.stockId,
      productId: li?.productId,
      serviceId: li?.serviceId,
      batchId: li?.batchId,
      remark: li?.remark ?? "",
      route: routeList?.find((rl) => rl?.name === li?.route) ?? li?.route,
      frequency: [],
      frequencyDuration: li?.frequencyDuration ?? [],
      packageUom: li?.packageUom,
      expiryDate: li?.expiryDate,
      batchNo: li?.batchNo,
      price: li?.price,
      mrpPrice: li?.mrpPrice,
    };
    if (
      Array.isArray(li?.frequencyDuration) &&
      li.frequencyDuration.length > 0
    ) {
      reqObj.frequency = li.frequencyDuration
        .map((freq) => frequncyCheckList?.find((f) => f?.apiValue === freq))
        ?.filter(Boolean);
    }
    return reqObj;
  };

  const handleAdd = () => {
    if (!values?.plName || values?.plName?.length === 0) {
      setValues((prev) => ({ ...prev, nameErr: true }));
      return;
    }

    setSelectedItems((prev) => [...prev, { ...values, nameErr: false }]);
    setValues(initValues);
    dispatch(
      showSnackBar({
        message: "Item added successfully",
        type: "success",
      }),
    );
  };

  const handleDone = () => {
    const hasCurrentDrug =
      !!values?.plName &&
      (typeof values?.plName === "string" ? values.plName.length > 0 : true);

    const itemsToSave = hasCurrentDrug
      ? [...selectedItems, { ...values, nameErr: false }]
      : selectedItems;

    if (itemsToSave.length === 0) {
      onClose();
      return;
    }

    const existingDetails =
      reqDet?.prescription?.[0]?.prescriptionDetails ?? [];
    const existingMapped = existingDetails.map(mapExistingPresToForm);
    const prescription = [...existingMapped, ...itemsToSave].map(
      buildPresPayloadItem,
    );

    const data = {
      status: "Pending",
      remark: reqDet?.prescription?.[0]?.remark ?? "",
      prescription,
      issue: "",
      paymentId: reqDet?.paymentId ?? null,
    };

    setIsSaving(true);
    const onSuccess = () => {
      setIsSaving(false);
      refreshClinicStocksToSession(dispatch);
      onApiCall();
      onClose();
    };
    const onFail = () => setIsSaving(false);

    if (reqDet?.prescription?.[0]?.prescriptionId) {
      dispatch(
        updatePrescriptionByIdWoLoader({
          prescriptionId: reqDet?.prescription?.[0]?.prescriptionId,
          data,
        }),
      ).then((res) => {
        if (res?.payload) onSuccess();
        else onFail();
      });
    } else {
      dispatch(
        createPrescriptionWoLoader({
          appointmentId: appDet?.appointmentId,
          data,
        }),
      ).then((res) => {
        if (res?.payload) onSuccess();
        else onFail();
      });
    }
  };

  const reqStck = values?.stockId ? getStockById(values?.stockId) : null;
  const packageUomLabel = getRightPackageUom(
    getBatch(values?.stockId, values?.batchId)?.packageUom,
    true,
  );
  const dosageField = (
    <CustomTextField
      fullWidth
      handleChange={(e) => onChangeValue("dosage", e?.target?.value)}
      value={values?.dosage}
      suffix={
        packageUomLabel?.length > 0 ? (
          <Custom3dotString
            str={`${packageUomLabel}`}
            length={9}
            className="fs12 font-medium"
            capitalize={false}
          />
        ) : (
          ""
        )
      }
      placeholder="e.g. 15 mg/kg"
    />
  );

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header="Add medication (Prescription)"
      modal
      modalWidth={40}
      reduceHeaderSize
      hideClose
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{ height: "calc(100vh - 75px)" }}
      >
        <Box p={3} flexGrow={1} overflow="auto">
          <Box mb={2.5}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              DRUG / MEDICATION
            </Typography>
            <SearchableSelect
              variant="procedure"
              list={getPresList() ?? []}
              value={values?.plName}
              error={values?.nameErr}
              helperText={values?.nameErr ? "Required" : ""}
              plusBtn
              mandatoryField
              isForProcedure
              showQtyNearExpiry
              allowAddNew
              handleChange={(e, value) => {
                onChangeValue("plName", value?.name ?? value?.value ?? "", {
                  stockId: value?.stockId ?? null,
                  productId: value?.productId ?? null,
                  serviceId: value?.serviceId ?? null,
                  type: value?.type ?? null,
                });
              }}
              maxFilteredOptions={100}
              placeholder="Search the drug formulary..."
            />
          </Box>

          <Grid container spacing={2} mb={2.5}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                STRENGTH / DOSE
              </Typography>
              {values?.stockId ? (
                <Tooltip
                  title={
                    <>
                      <Typography className="fs12" display="block">
                        Approximated Dosage: {reqStck?.dosage ?? "-"}
                      </Typography>
                      <Typography className="fs12" display="block">
                        Dosage Type: {reqStck?.dosageType ?? "-"}
                      </Typography>
                    </>
                  }
                >
                  <span style={{ display: "inline-block", width: "100%" }}>
                    {dosageField}
                  </span>
                </Tooltip>
              ) : (
                dosageField
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                FREQUENCY
              </Typography>
              {values?.customFrequency ? (
                <CustomTextField
                  fullWidth
                  handleChange={(e) =>
                    onChangeValue("frequency", e?.target?.value)
                  }
                  value={values?.frequency}
                />
              ) : (
                <CustomSelect
                  list={frequncyCheckList}
                  value={values?.frequency ?? []}
                  handleChange={(e) =>
                    onChangeValue("frequency", e?.target?.value)
                  }
                  multiSelectSearchCheck
                />
              )}
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2.5}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                ROUTE
              </Typography>
              <CustomSelect
                list={routeList}
                value={values?.route}
                search
                handleChange={(e, value) => onChangeValue("route", value)}
              />
            </Grid>

            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                DURATION (DAYS)
              </Typography>
              <CustomTextField
                fullWidth
                handleChange={(e) =>
                  onChangeValue("duration", e?.target?.value)
                }
                value={values?.duration}
              />
            </Grid>
          </Grid>

          <Box mb={2.5}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              INSTRUCTIONS
            </Typography>
            <CustomSelect
              list={mealTypes}
              value={values?.meal}
              select
              handleChange={(e) => onChangeValue("meal", e?.target?.value)}
            />
          </Box>
          {/* <Box mb={2.5}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              INSTRUCTIONS FOR OWNER (SIG)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder="e.g. Give with food, complete the full course"
              value={values?.sig}
              onChange={(e) => onChangeValue("i", e.target.value)}
              sx={{
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                },
              }}
            />
          </Box> */}

          <Box mb={2}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              REMARKS
            </Typography>
            <CustomTextField
              value={values?.remark}
              handleChange={(e) => onChangeValue("remark", e?.target?.value)}
              fullWidth
              placeholder="Notes (optional)"
            />
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.5}
          px={3}
          py={2}
          borderTop="1px solid #e0e0e0"
          backgroundColor="#fafafa"
        >
          <CustomButton
            text="Add to prescription"
            smallBtn
            disabled={isSaving}
            onClick={handleAdd}
          />
          <Button
            onClick={handleDone}
            variant="outlined"
            disabled={isSaving}
            sx={{
              color: AppColors.appPrimary || "#0054A6",
              borderColor: AppColors.appPrimary || "#0054A6",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              height: "32px",
            }}
          >
            Done
          </Button>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default AddPrescriptionDrawer;
